import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { needRequests } from 'redux/requests/need.requests';
import {
  selectNeedsIssueByIdFromMetadata,
} from 'redux/slices/metaSlice.slice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hook';
import { needsSelectors, patchNeedSuccess } from 'redux/slices/needs.slice';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import CloseableDialogTitle from './components/dialog-components/CloseableDialogTitle';
import SubmittableDialogActions from './components/dialog-components/SubmittableDialogActions';
import { FormBuilder } from '@act/storybook';
import { useToast } from 'react-components/app/ReusableToast';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FulfillNeedForm,
  fulfillNeedSchema,
} from '@act/shared/data-transfer-objects';
import useUnsavedChangesDialog from './hooks/useUnsavedChangesDialog.hook';
import { useAppDispatch } from 'redux/storeHooks';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { TrackingProvider } from 'react-components/tracking';

export interface FulfillNeedDialogUniqueProps {
  needId: number;
}

export type FulfillNeedDialogProps = GenericDialogProps<void> &
  FulfillNeedDialogUniqueProps;

export type FulfillNeedDialogCallbackInput = void;

export const FulfillNeedDialog = ({
  needId,
  open,
  setOpen,
  callback,
}: FulfillNeedDialogProps) => {
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const dispatch = useAppDispatch();
  const need = needsSelectors.useSelectNeedById(needId);
  const needMetadata = metaSliceSelectors.useSelectNeedsMetadata();
  const issue = selectNeedsIssueByIdFromMetadata(
    needMetadata?.data,
    need?.issueId
  );
  const [fulfillNeedResult, isLoading, patchNeed] = useApiCall(
    needRequests.patchNeed
  );

  const { control, handleSubmit, formState } = useForm<FulfillNeedForm>({
    defaultValues: {},
    resolver: zodResolver(fulfillNeedSchema),
  });

  const formConfig: FormConfig<FulfillNeedForm> = [
    {
      type: 'input',
      name: 'description',
      label: 'Description*',
      input: 'text',
      props: {
        required: true,
        multiline: true,
        minRows: 4,
        maxRows: 4,
        characterLimit: 500,
      },
    },
  ];

  async function handleSubmission(data: FulfillNeedForm) {
    patchNeed({
      params: { needId: needId },
      body: {
        fulfilled: true,
        comment: data.description,
      },
    });
  }

  useEffect(() => {
    if (fulfillNeedResult) {
      dispatch(patchNeedSuccess(fulfillNeedResult));
      toast('Need fulfilled successfully');
      setOpen(false);
      callback();
    }
  }, [fulfillNeedResult]);

  const { isDirty } = formState;
  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
        callback();
      });
    } else {
      setOpen(false);
      callback();
    }
  };

  return (
    <TrackingProvider contextName="fulfill-need-dialog">
      <Dialog open={open} onClose={handleClose}>
        <CloseableDialogTitle
          title={'Mark as fulfilled'}
          handleClose={handleClose}
        />
        <DialogContent>
          <Box sx={{ padding: '16px 0 16px' }}>
            <Typography variant="titleMedium">
              Are you sure you want to mark {issue?.label} as filled?
            </Typography>
          </Box>
          <Box sx={{ paddingBottom: '16px' }}>
            <Typography variant="bodyMedium">
              A need is considered fulfilled when the member is no longer
              experiencing any issues related to the need. Add any relevant
              details below to fulfill this need.
            </Typography>
          </Box>
          <Box sx={{ padding: '16px 0 16px' }}>
            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
            />
          </Box>
        </DialogContent>
        <SubmittableDialogActions
          submissionText="Save and close"
          isValid={formState.isValid}
          disabled={isLoading}
          handleClose={handleClose}
          handleSubmission={handleSubmit(handleSubmission)}
        />
      </Dialog>
    </TrackingProvider>
  );
};
